import type { BoardCase, BoardTask } from "@/api/board/boardCaseFields";
import { foldSearch } from "@/api/cases/foldSearch";
import { WIDE_VIEWPORT_BREAKPOINT } from "@/constants/layout";
import { formatDateDE, formatTimestampDE } from "@/utils/isoDate";

export type CaseTableDensity = "desktop" | "tablet" | "phone";

export type CaseBoardRank = "missing" | "overdue" | "due" | "open" | "rest";

export type CaseBoardStatus = {
  rank: CaseBoardRank;
  /** Sichtbarer Text. Ohne Feed-Status „–“, nicht nur eine Farbe. */
  label: string;
  tone: "warning" | "danger" | "neutral" | null;
  sort: number;
};

const SORT: Record<CaseBoardRank, number> = {
  missing: 0,
  overdue: 1,
  due: 2,
  open: 3,
  rest: 4,
};

/** Chip für Fälle ohne Art. */
export const CASE_SPECIES_UNSET = "Ohne Art";

/** Gemessene Containerbreite, nicht die Fensterbreite. Desktop bleibt 900. */
export const CASE_TABLE_DESKTOP_MIN = 900;

/** Feste Sidebar ab dem breiten Viewport. Siehe app/(app)/_layout.tsx. */
export const BOARD_SIDEBAR_WIDE = 288;

export function caseTableDensity(width: number): CaseTableDensity {
  if (width >= CASE_TABLE_DESKTOP_MIN) return "desktop";
  if (width >= CASE_TABLE_TABLET_MIN) return "tablet";
  return "phone";
}

export const CASE_TABLE_COLUMN_KEYS: Record<CaseTableDensity, readonly string[]> = {
  desktop: ["tier", "art", "gewicht", "pflegestelle", "aufgabe", "status", "aktionen"],
  // Bisher fünf Spalten, jetzt zusätzlich Art.
  tablet: ["tier", "art", "gewicht", "aufgabe", "status", "akte"],
  phone: ["zeile"],
};

/**
 * Untergrenze je Spalte. Tier 150, Art 88, Gewicht 100, Aufgabe 140, Pflegestelle 88.
 * Die Aufgabe trägt das Label und die Uhrzeit in getrennten Zeilen; die Uhrzeit
 * bleibt in 140 px ganz. Die Tablet-Schwelle ist die Summe dieses Bedarfs.
 */
export const CASE_COLUMN_MIN_WIDTH: Record<string, number> = {
  tier: 150,
  art: 88,
  gewicht: 100,
  pflegestelle: 88,
  aufgabe: 140,
  status: 96,
  aktionen: 168,
  akte: 72,
};

/** flexGrow der flexiblen Spalten. Status und Aktionen bleiben fest. */
export const CASE_COLUMN_FLEX: Record<string, number> = {
  tier: 1.4,
  art: 0.6,
  gewicht: 1,
  pflegestelle: 1,
  aufgabe: 1.6,
};

const COLUMN_GAP = 8;
const ROW_PAD = 16;

export function caseTableReservedWidth(density: Exclude<CaseTableDensity, "phone">): number {
  const keys = CASE_TABLE_COLUMN_KEYS[density];
  const columns = keys.reduce((sum, key) => sum + (CASE_COLUMN_MIN_WIDTH[key] ?? 0), 0);
  return columns + Math.max(keys.length - 1, 0) * COLUMN_GAP + ROW_PAD;
}

/** Tablet erst ab dem reservierten Bedarf. Dieselbe Zahl wie caseTableReservedWidth("tablet"). */
export const CASE_TABLE_TABLET_MIN = caseTableReservedWidth("tablet");

/**
 * Breite der Tabelle im Board: Fenster abzüglich Sidebar (nur ab 900) und der
 * Innenabstände von Screen (lg) und Karte (sm).
 */
export function boardTableContainerWidth(
  windowWidth: number,
  padding: { screen: number; card: number } = { screen: 16, card: 8 },
): number {
  const sidebar = windowWidth >= WIDE_VIEWPORT_BREAKPOINT ? BOARD_SIDEBAR_WIDE : 0;
  return windowWidth - sidebar - padding.screen * 2 - padding.card * 2;
}

/**
 * Rang der Zeile. fällig und überfällig kommen aus open_tasks[].status.
 * Der Client leitet sie nicht aus due_at, der Uhr oder dem Berliner Tag ab.
 * fehlt rechnet nur der Client: medication_dose, nicht gegeben (die Aufgabe
 * steht in open_tasks) und due_at <= jetzt, strikt, ohne Karenz. Eine Dosis
 * um 20:00 bei Abruf um 08:00 bleibt beim Backend-Status. Eine Dosis zur
 * selben Minute wie jetzt ist fehlt, auch wenn das Backend due oder overdue sagt.
 * Annahme: open_tasks enthält nur nicht erledigte Aufgaben. list_control_board
 * (Migration 20261015100000, plan_rows) nimmt eine Medikamentenzeile nur auf,
 * wenn NOT private_helpers.regular_occurrence_done. Eine gegebene Dosis fehlt
 * deshalb im Array; die Mitgliedschaft gilt als „nicht gegeben“.
 * derive_case_open_tasks (20260808120000) filtert Gaben genauso, wird vom
 * Board aber nicht mehr aufgerufen.
 * Fehlt status, gilt für eine Nicht-Medikament-Aufgabe mit due_at < jetzt
 * noch „überfällig“ (alte Daten ohne status). Sonst ist ein fehlender oder
 * unbekannter Status „n offen“, auch bei leerem open_tasks und nur
 * open_task_count. Ohne Aufgabe und ohne Zähler bleibt der Rest.
 * Die Spalte „Nächste Aufgabe“ zeigt die Aufgabe, die den Rang bestimmt.
 * Rang: fehlt › überfällig › fällig › n offen › Rest, danach die Fallnummer.
 * Die Home-KPI zählt Fälle nach diesem Rang: fehlt über medicationDoseIsMissing,
 * überfällig und fällig nur über open_tasks[].status (caseBoardStatus).
 * Dieselbe Uhr wie die Tabelle, damit die Kachel der Zeilenzahl entspricht.
 */
export function taskDueAt(task: Pick<BoardTask, "due_at" | "planned_at">): string {
  const due = task.due_at?.trim() ?? "";
  if (due) return due;
  return task.planned_at?.trim() ?? "";
}

function dueAtMs(task: Pick<BoardTask, "due_at" | "planned_at">): number | null {
  const raw = taskDueAt(task);
  if (!raw) return null;
  const ms = Date.parse(raw);
  return Number.isFinite(ms) ? ms : null;
}

function clockMs(now: number | string | Date): number {
  const ms = now instanceof Date ? now.getTime() : typeof now === "number" ? now : Date.parse(now);
  return Number.isFinite(ms) ? ms : Date.now();
}

export function medicationDoseIsMissing(task: BoardTask, nowMs: number): boolean {
  if (task.kind !== "medication_dose") return false;
  const due = dueAtMs(task);
  if (due == null) return false;
  return due <= nowMs;
}

function otherTaskIsOverdue(task: BoardTask, nowMs: number): boolean {
  if (task.kind === "medication_dose") return false;
  if (task.status === "overdue") return true;
  if (task.status === "due") return false;
  const due = dueAtMs(task);
  return due != null && due < nowMs;
}

function drivesOverdue(task: BoardTask, nowMs: number): boolean {
  if (medicationDoseIsMissing(task, nowMs)) return false;
  if (task.status === "overdue") return true;
  return otherTaskIsOverdue(task, nowMs);
}

function compareDue(a: BoardTask, b: BoardTask): number {
  const left = dueAtMs(a);
  const right = dueAtMs(b);
  if (left == null && right == null) return 0;
  if (left == null) return 1;
  if (right == null) return -1;
  return left - right;
}

/** Die Aufgabe, die den Zeilenstatus trägt. Früheste Fälligkeit innerhalb des Rangs. */
export function drivingBoardTask(
  tasks: readonly BoardTask[],
  now: number | string | Date = Date.now(),
): BoardTask | null {
  const nowMs = clockMs(now);
  const missing = tasks.filter((task) => medicationDoseIsMissing(task, nowMs)).sort(compareDue);
  if (missing.length > 0) return missing[0] ?? null;
  const overdue = tasks.filter((task) => drivesOverdue(task, nowMs)).sort(compareDue);
  if (overdue.length > 0) return overdue[0] ?? null;
  const due = tasks
    .filter((task) => task.status === "due" && !medicationDoseIsMissing(task, nowMs))
    .sort(compareDue);
  if (due.length > 0) return due[0] ?? null;
  if (tasks.length === 0) return null;
  return [...tasks].sort(compareDue)[0] ?? null;
}

export function resolveCaseBoardStatus(
  tasks: readonly BoardTask[],
  now: number | string | Date = Date.now(),
  openCount = tasks.length,
): CaseBoardStatus {
  const nowMs = clockMs(now);
  if (tasks.some((task) => medicationDoseIsMissing(task, nowMs))) {
    return status("missing", "fehlt", "danger");
  }
  if (tasks.some((task) => drivesOverdue(task, nowMs))) {
    return status("overdue", "überfällig", "warning");
  }
  if (tasks.some((task) => task.status === "due" && !medicationDoseIsMissing(task, nowMs))) {
    return status("due", "fällig", "neutral");
  }
  const count = openCount > 0 ? openCount : tasks.length;
  if (count > 0) return status("open", `${count} offen`, null);
  return status("rest", "–", null);
}

export function caseBoardStatus(
  row: Pick<BoardCase, "open_tasks" | "open_task_count">,
  now: number | string | Date = Date.now(),
): CaseBoardStatus {
  return resolveCaseBoardStatus(row.open_tasks ?? [], now, row.open_task_count ?? 0);
}

function status(
  rank: CaseBoardRank,
  label: string,
  tone: CaseBoardStatus["tone"],
): CaseBoardStatus {
  return { rank, label, tone, sort: SORT[rank] };
}

function compareCaseNumber(a: string | null, b: string | null): number {
  const left = a?.trim() ?? "";
  const right = b?.trim() ?? "";
  if (!left && !right) return 0;
  if (!left) return 1;
  if (!right) return -1;
  return left.localeCompare(right, "de", { numeric: true });
}

export function compareBoardCases(
  a: BoardCase,
  b: BoardCase,
  now?: number | string | Date,
): number {
  const byStatus = caseBoardStatus(a, now).sort - caseBoardStatus(b, now).sort;
  if (byStatus !== 0) return byStatus;
  const byNumber = compareCaseNumber(a.case_number, b.case_number);
  if (byNumber !== 0) return byNumber;
  return a.case_id.localeCompare(b.case_id);
}

export function sortBoardCases(
  rows: readonly BoardCase[],
  now?: number | string | Date,
): BoardCase[] {
  return [...rows].sort((a, b) => compareBoardCases(a, b, now));
}

export function caseAnimalLabel(row: Pick<BoardCase, "case_number" | "name">): string {
  const number = row.case_number?.trim() || "";
  const name = row.name?.trim() || "";
  if (number && name) return `${number} · ${name}`;
  return number || name || "–";
}

export function caseSpeciesLabel(
  row: Pick<BoardCase, "species_name" | "species_unassigned">,
): string {
  const name = row.species_name?.trim() || "";
  if (row.species_unassigned) {
    return name ? `${name} · Nicht zugeordnet` : "Nicht zugeordnet";
  }
  return name || "–";
}

export function caseSiteLabel(row: Pick<BoardCase, "current_care_site_name">): string {
  return row.current_care_site_name?.trim() || "–";
}

function formatGrams(value: number): string {
  const body = Number.isInteger(value)
    ? String(value)
    : value.toLocaleString("de-DE", { maximumFractionDigits: 1 });
  return `${body} g`;
}

/** Differenz letztes minus vorheriges Gewicht. Ohne Vorgänger null. */
export function weightDeltaGrams(
  last: number | null | undefined,
  previous: number | null | undefined,
): number | null {
  if (last == null || previous == null) return null;
  if (!Number.isFinite(last) || !Number.isFinite(previous)) return null;
  return last - previous;
}

export function formatWeightDelta(delta: number): string {
  const body = formatGrams(Math.abs(delta));
  if (delta > 0) return `(+${body})`;
  if (delta < 0) return `(\u2212${body})`;
  return `(\u00b10 g)`;
}

/** Gramm und Differenz, ohne Datum. Für die zweite Phone-Zeile. */
export function formatCaseWeightShort(row: Pick<
  BoardCase,
  "last_weight_grams" | "previous_weight_grams"
>): string {
  if (row.last_weight_grams == null) return "–";
  const parts = [formatGrams(row.last_weight_grams)];
  const delta = weightDeltaGrams(row.last_weight_grams, row.previous_weight_grams);
  if (delta != null) parts.push(formatWeightDelta(delta));
  return parts.join(" ");
}

/** z. B. „412 g (+18 g) · 14.10.2026“. Ohne Gewicht „–“. */
export function formatCaseWeight(row: Pick<
  BoardCase,
  "last_weight_grams" | "previous_weight_grams" | "last_weight_at"
>): string {
  if (row.last_weight_grams == null) return "–";
  const parts = [formatGrams(row.last_weight_grams)];
  const delta = weightDeltaGrams(row.last_weight_grams, row.previous_weight_grams);
  if (delta != null) parts.push(formatWeightDelta(delta));
  const date = row.last_weight_at ? formatDateDE(row.last_weight_at) : "";
  if (date && date !== "–") parts.push(`· ${date}`);
  return parts.join(" ");
}

/** Label und Uhrzeit getrennt, damit die Uhrzeit nicht in die nächste Zeile bricht. */
export function nextTaskLines(
  row: Pick<BoardCase, "open_tasks" | "next_due_at">,
  now: number | string | Date = Date.now(),
): { label: string; when: string } | null {
  const current = drivingBoardTask(row.open_tasks ?? [], now);
  if (current) {
    const when = formatTimestampDE(taskDueAt(current));
    return { label: current.label, when: when === "–" ? "" : when };
  }
  if (row.next_due_at) {
    const when = formatTimestampDE(row.next_due_at);
    return { label: "", when: when === "–" ? "" : when };
  }
  return null;
}

/** Phone-Unterzeile. Leere Teile („–“) fallen weg, damit keine Kette aus Strichen steht. */
export function phoneCaseDetail(
  row: Pick<
    BoardCase,
    | "species_name"
    | "species_unassigned"
    | "last_weight_grams"
    | "previous_weight_grams"
    | "open_tasks"
    | "next_due_at"
  >,
  now: number | string | Date = Date.now(),
): string {
  const parts = [caseSpeciesLabel(row), formatCaseWeightShort(row), formatNextTask(row, now)].filter(
    (part) => part.trim() !== "" && part.trim() !== "–",
  );
  return parts.join(" · ");
}

/** Dieselbe Aufgabe wie der Zeilenstatus, sonst next_due_at, sonst „–“. */
export function formatNextTask(
  row: Pick<BoardCase, "open_tasks" | "next_due_at">,
  now: number | string | Date = Date.now(),
): string {
  const lines = nextTaskLines(row, now);
  if (!lines) return "–";
  if (lines.label && lines.when) return `${lines.label} · ${lines.when}`;
  return lines.label || lines.when || "–";
}

export type BoardKpiKey = "due" | "overdue" | "missing" | "active";

export const BOARD_KPI_LABEL: Record<BoardKpiKey, string> = {
  due: "Heute fällig",
  overdue: "Überfällig",
  missing: "Medikament fehlt",
  active: "Aktive Fälle",
};

/** 0 und ab 2 Plural, genau 1 Singular. */
export function formatCaseCount(count: number): string {
  return `${count} ${count === 1 ? "Fall" : "Fälle"}`;
}

/**
 * Kachelzahlen. due/overdue/missing sind Fallzahlen des Zeilenrangs, nicht
 * Aufgabenarten. active zählt in_care und hibernating, unabhängig vom Rang.
 */
export function boardKpiCounts(
  rows: readonly Pick<BoardCase, "open_tasks" | "open_task_count" | "status">[],
  now: number | string | Date = Date.now(),
): Record<BoardKpiKey, number> {
  const counts: Record<BoardKpiKey, number> = {
    due: 0,
    overdue: 0,
    missing: 0,
    active: 0,
  };
  for (const row of rows) {
    const rank = caseBoardStatus(row, now).rank;
    if (rank === "due" || rank === "overdue" || rank === "missing") counts[rank] += 1;
    if (row.status === "in_care" || row.status === "hibernating") counts.active += 1;
  }
  return counts;
}

function matchesKpi(row: BoardCase, kpi: BoardKpiKey, now: number | string | Date): boolean {
  if (kpi === "active") return row.status === "in_care" || row.status === "hibernating";
  return caseBoardStatus(row, now).rank === kpi;
}

export type CaseTableFilter = {
  species: readonly string[];
  site: string | null;
  kpi?: BoardKpiKey | null;
};

export function distinctLabels(values: readonly (string | null | undefined)[]): string[] {
  const seen = new Set<string>();
  for (const value of values) {
    const label = value?.trim();
    if (label) seen.add(label);
  }
  return [...seen].sort((a, b) => a.localeCompare(b, "de"));
}

function hasSpeciesMark(value: string): boolean {
  return /[äöüÄÖÜß]/.test(value);
}

/** Gleiche Faltung: Umlaute behalten, sonst die nach de stabil kleinere Schreibweise. */
function preferSpeciesLabel(current: string, next: string): string {
  const currentMark = hasSpeciesMark(current);
  const nextMark = hasSpeciesMark(next);
  if (currentMark !== nextMark) return nextMark ? next : current;
  const cmp = current.localeCompare(next, "de", { numeric: true, sensitivity: "variant" });
  if (cmp !== 0) return cmp < 0 ? current : next;
  return current < next ? current : next;
}

/** Ein Chip je foldSearch-Schlüssel. Ohne echte Art kein Chip, auch nicht „Ohne Art“. */
export function speciesFilterOptions(rows: readonly Pick<BoardCase, "species_name">[]): string[] {
  const byFold = new Map<string, string>();
  let unset = false;
  for (const row of rows) {
    const name = row.species_name?.trim() ?? "";
    if (!name) {
      unset = true;
      continue;
    }
    const key = foldSearch(name);
    const current = byFold.get(key);
    if (current == null) byFold.set(key, name);
    else byFold.set(key, preferSpeciesLabel(current, name));
  }
  if (byFold.size === 0) return [];
  const labels = [...byFold.values()].sort((a, b) => a.localeCompare(b, "de", { numeric: true }));
  if (unset) labels.push(CASE_SPECIES_UNSET);
  return labels;
}

function speciesKey(name: string | null | undefined): string {
  const trimmed = name?.trim() ?? "";
  return trimmed ? foldSearch(trimmed) : CASE_SPECIES_UNSET;
}

export function filterBoardCases(
  rows: readonly BoardCase[],
  filter: CaseTableFilter,
  now: number | string | Date = Date.now(),
): BoardCase[] {
  const wanted = new Set(filter.species.map((value) => speciesKey(value === CASE_SPECIES_UNSET ? "" : value)));
  const kpi = filter.kpi ?? null;
  return rows.filter((row) => {
    if (wanted.size > 0 && !wanted.has(speciesKey(row.species_name))) return false;
    if (filter.site) {
      if ((row.current_care_site_name?.trim() ?? "") !== filter.site) return false;
    }
    if (kpi && !matchesKpi(row, kpi, now)) return false;
    return true;
  });
}

/** Gewählte Art oder Pflegestelle verwerfen, wenn sie in den Daten nicht mehr vorkommt. */
export function pruneCaseFilter(
  filter: CaseTableFilter,
  rows: readonly BoardCase[],
): CaseTableFilter {
  const speciesOptions = new Set(speciesFilterOptions(rows));
  const sites = new Set(distinctLabels(rows.map((row) => row.current_care_site_name)));
  const species = filter.species.filter((value) => speciesOptions.has(value));
  const site = filter.site && sites.has(filter.site) ? filter.site : null;
  if (species.length === filter.species.length && site === filter.site) return filter;
  return { species, site };
}

export function activeFilterLabel(filter: CaseTableFilter): string | null {
  const parts: string[] = [];
  if (filter.species.length > 0) parts.push(`Art: ${filter.species.join(", ")}`);
  if (filter.site) parts.push(`Pflegestelle: ${filter.site}`);
  if (filter.kpi) parts.push(BOARD_KPI_LABEL[filter.kpi]);
  return parts.length > 0 ? parts.join(" · ") : null;
}
